import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatCurrency, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

const COMB_LABELS = {
    diesel: "Diesel Comum (S500)", diesel_s10: "Diesel S10",
    gasolina: "Gasolina Comum", etanol: "Etanol", gnv: "GNV", outro: "Outro",
};
const COMB_COLORS = {
    diesel: "#b45309", diesel_s10: "#dc2626",
    gasolina: "#059669", etanol: "#16a34a", gnv: "#0284c7", outro: "#6b7280",
};
const EMPR_LABELS = {
    km117: "Empreendimento KM 117", rio_cupari: "Rio Cupari",
    sede: "Sede COOMFLONA", setor_social: "Setor Social",
    nao_madeireiro: "Não Madeireiro", outro: "Outro",
};

// SVG desenho do posto de combustível
function GasStationSVG({ color = "#dc2626", size = 100 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            {/* Base do terreno */}
            <rect x="5" y="90" width="90" height="6" fill="#4b5563" />
            {/* Corpo da bomba (retângulo grande) */}
            <rect x="30" y="35" width="30" height="55" fill={color} stroke="#000" strokeWidth="1.5" rx="2" />
            {/* Topo da bomba */}
            <rect x="27" y="30" width="36" height="6" fill="#1f2937" stroke="#000" strokeWidth="1.5" rx="1" />
            {/* Display digital */}
            <rect x="34" y="42" width="22" height="12" fill="#111827" stroke="#374151" strokeWidth="0.5" />
            <text x="45" y="50" fontSize="6" fill="#22c55e" textAnchor="middle" fontFamily="monospace" fontWeight="bold">R$ 000,00</text>
            {/* Botão / seletor */}
            <circle cx="45" cy="65" r="4" fill="#fbbf24" stroke="#000" strokeWidth="1" />
            <rect x="36" y="72" width="18" height="3" fill="#e5e7eb" stroke="#000" strokeWidth="0.5" />
            <rect x="36" y="78" width="18" height="3" fill="#e5e7eb" stroke="#000" strokeWidth="0.5" />
            {/* Mangueira */}
            <path d="M 60 45 Q 75 45 75 60 Q 75 75 68 82" stroke="#1f2937" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            {/* Bico */}
            <rect x="63" y="80" width="10" height="6" fill="#374151" stroke="#000" strokeWidth="0.8" rx="1" transform="rotate(20 68 83)" />
            {/* Cobertura */}
            <polygon points="18,25 82,25 78,15 22,15" fill="#dc2626" stroke="#000" strokeWidth="1.5" />
            <rect x="16" y="24" width="68" height="4" fill="#7f1d1d" stroke="#000" strokeWidth="1" />
            {/* Pilar */}
            <rect x="12" y="28" width="4" height="62" fill="#6b7280" stroke="#000" strokeWidth="1" />
            <rect x="84" y="28" width="4" height="62" fill="#6b7280" stroke="#000" strokeWidth="1" />
        </svg>
    );
}

export default function FuelReceiptPrint() {
    const { id } = useParams();
    const { user } = useAuth();
    const [receipt, setReceipt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/fuel-receipts/${id}`)
            .then((res) => setReceipt(res.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [id]);

    const doPrint = () => window.print();
    const today = new Date().toLocaleDateString("pt-BR");

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
    }
    if (error || !receipt) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col gap-4">
                <div className="text-black">Nota não encontrada</div>
                <Button onClick={() => window.close()}>Fechar</Button>
            </div>
        );
    }

    const combColor = COMB_COLORS[receipt.tipo_combustivel] || "#dc2626";
    const combLabel = COMB_LABELS[receipt.tipo_combustivel] || receipt.tipo_combustivel;

    return (
        <div className="print-root min-h-screen bg-gray-100 p-8" data-testid="fuel-print-page">
            <div className="no-print max-w-3xl mx-auto mb-4 flex items-center justify-between">
                <Button variant="outline" onClick={() => window.close()}>Fechar</Button>
                <Button onClick={doPrint} className="gap-2"><Printer className="w-4 h-4" /> Imprimir</Button>
            </div>

            <div className="ficha max-w-3xl mx-auto bg-white shadow-2xl relative overflow-hidden">
                <div className="h-2" style={{ background: `linear-gradient(90deg, ${combColor} 0%, #059669 100%)` }} />

                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-gray-800 bg-gradient-to-r from-emerald-50 to-white">
                    <div className="w-16 h-16 bg-emerald-900 rounded-lg flex items-center justify-center p-0.5 shrink-0 shadow-md border-2 border-emerald-800">
                        <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex-1">
                        <div className="font-extrabold text-base leading-tight text-emerald-900">COOPERATIVA MISTA DA FLONA DO TAPAJÓS</div>
                        <div className="text-xs font-bold text-emerald-800 tracking-wide">COOMFLONA — Setor de Transporte</div>
                        <div className="text-[10px] mt-0.5 text-black">CNPJ 07.315.165/0001-00</div>
                    </div>
                    <div className="text-right">
                        <div className="inline-block px-2 py-1 rounded text-[10px] font-bold text-white" style={{ background: combColor }}>NOTA DE COMBUSTÍVEL</div>
                        {receipt.numero_nota && (
                            <>
                                <div className="text-[9px] text-black mt-1.5">Nº NF</div>
                                <div className="text-sm font-extrabold font-mono text-black">{receipt.numero_nota}</div>
                            </>
                        )}
                    </div>
                </div>

                <div className="text-white text-center py-2" style={{ background: combColor }}>
                    <div className="font-extrabold text-base">CONTROLE DE ABASTECIMENTO</div>
                </div>

                <div className="p-5 space-y-3 text-sm">
                    {/* Bloco visual: desenho do posto + valor grande */}
                    <div className="flex items-center gap-4 rounded-lg border-2 p-3" style={{ borderColor: combColor, background: `${combColor}10` }}>
                        <div className="shrink-0">
                            <GasStationSVG color={combColor} size={100} />
                        </div>
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-widest text-black font-bold">Posto</div>
                            <div className="text-2xl font-extrabold text-black leading-tight">{receipt.posto}</div>
                            <div className="mt-1 flex flex-wrap gap-2 items-center">
                                <span className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white" style={{ background: combColor }}>
                                    {combLabel}
                                </span>
                                <span className="text-xs text-black">Data: <strong>{formatDateBR(receipt.data)}</strong></span>
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="text-[10px] uppercase tracking-widest text-black font-bold">Valor total</div>
                            <div className="text-3xl font-extrabold" style={{ color: combColor }}>{formatCurrency(receipt.valor_total)}</div>
                        </div>
                    </div>

                    <div>
                        <SectionTitle color={combColor}>Detalhes do abastecimento</SectionTitle>
                        <div className="grid grid-cols-3 gap-3 mt-1.5">
                            <Field label="Litros" value={receipt.litros ? `${receipt.litros.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} L` : "—"} />
                            <Field label="Valor por litro" value={receipt.valor_litro ? `R$ ${receipt.valor_litro.toFixed(3).replace('.', ',')}` : "—"} />
                            <Field label="KM do veículo" value={receipt.km_atual != null ? `${receipt.km_atual.toLocaleString("pt-BR")} km` : "—"} />
                        </div>
                    </div>

                    <div>
                        <SectionTitle color={combColor}>Veículo</SectionTitle>
                        <div className="grid grid-cols-2 gap-3 mt-1.5">
                            <Field label="Placa" value={<span className="font-mono">{receipt.veiculo_placa}</span>} />
                            <Field label="Modelo" value={receipt.veiculo_modelo || "—"} />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-1.5">
                            <Field label="Motorista" value={receipt.motorista_nome || "—"} />
                            <Field label="Empreendimento" value={<strong className="text-emerald-800">{EMPR_LABELS[receipt.empreendimento]}</strong>} />
                        </div>
                    </div>

                    {receipt.observacoes && (
                        <div>
                            <SectionTitle color={combColor}>Observações</SectionTitle>
                            <div className="mt-1 text-sm whitespace-pre-wrap bg-yellow-50 border-l-4 border-yellow-500 rounded p-2 text-black">{receipt.observacoes}</div>
                        </div>
                    )}
                </div>

                <div className="px-5 pb-3 pt-8 grid grid-cols-2 gap-3">
                    <div className="text-center">
                        <div className="border-t-2 border-black pt-0.5 text-[10px] font-bold text-black">Motorista</div>
                        <div className="text-[9px] text-black mt-0.5">{receipt.motorista_nome || "—"}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t-2 border-black pt-0.5 text-[10px] font-bold text-black">Setor de Transporte</div>
                        <div className="text-[9px] text-black mt-0.5">{user?.name || "—"}</div>
                    </div>
                </div>

                <div className="px-5 py-1.5 border-t border-gray-400 bg-gray-100 flex justify-between items-center text-[9px] text-black">
                    <div>Emitido em: <strong>{today}</strong></div>
                    <div>Sistema desenvolvido por <strong>Railan Farias</strong> (Cooperado)</div>
                </div>

                <div className="h-1" style={{ background: `linear-gradient(90deg, #059669 0%, ${combColor} 100%)` }} />
            </div>

            <style>{`
                @media print {
                    body { background: white !important; }
                    .no-print { display: none !important; }
                    .print-root { padding: 0 !important; background: white !important; }
                    .ficha { box-shadow: none !important; max-width: 100% !important; width: 100% !important; page-break-inside: avoid; }
                    @page { size: A4 portrait; margin: 8mm; }
                }
            `}</style>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-black font-bold">{label}</div>
            <div className="mt-0.5 font-bold text-black border-b-2 border-gray-700 pb-0.5 min-h-[20px]">{value || "—"}</div>
        </div>
    );
}

function SectionTitle({ children, color }) {
    return (
        <div className="text-[11px] font-extrabold uppercase tracking-widest pb-0.5 border-b-2" style={{ color, borderColor: color }}>{children}</div>
    );
}
